import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Plus, Trash2, Info, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FileUploadModal } from '@/components/FileUploadModal'
import { FilePreviewModal, type PreviewRow } from '@/components/FilePreviewModal'
import { type IngestionTaskStatus } from '@/lib/api'
import { useChatbots, useChatbotQnAFiles, useChatbotFiles, useIngestionTasks, useUploadQnAFile, useDeleteQnAFile } from '@/lib/queries'
import { LoadingModal } from '@/components/LoadingModal'
import { formatDateTime } from '@/lib/utils'
import readXlsxFile from 'read-excel-file'
import { Input } from '@/components/ui/input'

export function FnA() {
    const [selectedChatbot, setSelectedChatbot] = useState<string>("")

    const { data: chatbots = [] } = useChatbots()
    const { data: files = [] } = useChatbotQnAFiles(selectedChatbot)
    const { data: chatbotDocs = [] } = useChatbotFiles(selectedChatbot)
    const chatbotDocNames = chatbotDocs.map(d => d.filename)
    const { data: ingestionData } = useIngestionTasks(selectedChatbot || undefined, 'QNA')
    const uploadMutation = useUploadQnAFile(selectedChatbot)
    const deleteMutation = useDeleteQnAFile(selectedChatbot)

    const ingestionByFileId: Record<string, { status: IngestionTaskStatus; errorMessage?: string | null }> = {}
    if (ingestionData) {
        for (const task of ingestionData.tasks) {
            ingestionByFileId[String(task.document_id)] = {
                status: task.status,
                errorMessage: task.error_message,
            }
        }
    }

    const [isUploadOpen, setIsUploadOpen] = useState(false)
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [currentFile, setCurrentFile] = useState<File | null>(null)
    const [previewData, setPreviewData] = useState<PreviewRow[]>([])
    const [validationError, setValidationError] = useState<string[] | undefined>()
    const [searchQuery, setSearchQuery] = useState("")

    const handleImport = async (file: File) => {
        try {
            const rows = await readXlsxFile(file)

            if (rows.length - 1 > 5000) {
                alert("Exceeding 5000 rows limitation. Please select another file")
                return
            }

            if (rows.length < 2) {
                alert("File is empty or missing headers")
                return
            }

            const headers = rows[0].map(h => String(h).trim())
            const requiredColumns = ['Question', 'Expected answer', 'Source', 'Page']

            // Validate headers
            for (const col of requiredColumns) {
                if (!headers.includes(col)) {
                    alert(`${col} missing`)
                    return
                }
            }

            for (const header of headers) {
                if (!requiredColumns.includes(header)) {
                    alert("Invalid column")
                    return
                }
            }

            const headerMap = headers.reduce((acc, curr, idx) => ({ ...acc, [curr]: idx }), {} as Record<string, number>)
            const data: PreviewRow[] = []
            let errors: string[] = []

            for (let i = 1; i < rows.length; i++) {
                const row = rows[i]
                const question = row[headerMap['Question']]
                const answer = row[headerMap['Expected answer']]
                const source = row[headerMap['Source']]
                const page = row[headerMap['Page']]
                const rowNum = i + 1 // Excel row number

                if (!question || String(question).trim() === '') {
                    errors.push(`Question of row ${rowNum} is empty`)
                }
                if (!answer || String(answer).trim() === '') {
                    errors.push(`Answer of row ${rowNum} is empty`)
                }

                const sourceStr = source ? String(source).trim() : ''
                const pageStr = page ? String(page).trim() : ''

                if (sourceStr === '' && pageStr !== '') {
                    errors.push(`Page of row ${rowNum} must be empty when Source is empty`)
                }
                if (sourceStr !== '') {
                    if (!chatbotDocNames.includes(sourceStr)) {
                        errors.push(`Source of row ${rowNum} is not valid`)
                    }
                    if (pageStr !== '') {
                        const pageNum = Number(pageStr)
                        if (!/^\d+$/.test(pageStr) || pageNum <= 0) {
                            errors.push(`Page of row ${rowNum} is not a valid page number`)
                        }
                    }
                }

                data.push({
                    question: String(question || ''),
                    answer: String(answer || ''),
                    source: sourceStr,
                    page: pageStr
                })
            }

            setPreviewData(data)
            setValidationError(errors.length > 0 ? errors : undefined)
            setCurrentFile(file)
            setIsUploadOpen(false)
            setIsPreviewOpen(true)
        } catch (e) {
            console.error(e)
            alert("Failed to parse Excel file")
        }
    }

    const handleSave = async () => {
        if (currentFile && selectedChatbot) {
            try {
                await uploadMutation.mutateAsync(currentFile)
                setIsPreviewOpen(false)
                setCurrentFile(null)
                setPreviewData([])
                setValidationError(undefined)
            } catch (err) {
                alert(`Failed to save: ${err instanceof Error ? err.message : "Unknown error"}`)
            }
        }
    }

    const handleDelete = async (id: string) => {
        if (selectedChatbot && confirm("Are you sure you want to delete this file?")) {
            try {
                await deleteMutation.mutateAsync(id)
            } catch (err) {
                alert(`Failed to delete: ${err instanceof Error ? err.message : "Unknown error"}`)
            }
        }
    }

    const filteredChatbots = chatbots.filter(bot =>
        bot.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bot.id.toLowerCase().includes(searchQuery.toLowerCase())
    )

    return (
        <div className="p-8 max-w-[1400px]">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">QnA Enhancement</h1>
                <p className="text-gray-600">Upload and manage knowledge base files to improve the accuracy and response quality of your AI chatbots.</p>
            </div>

            <div className="flex justify-between items-center mb-8">
                <Select value={selectedChatbot} onValueChange={setSelectedChatbot} onOpenChange={(open) => { if (!open) setSearchQuery("") }}>
                    <SelectTrigger
                        className="w-[500px] xl:w-[600px] bg-white h-auto min-h-9 text-left [&>span]:line-clamp-none [&>span]:whitespace-normal [&>span]:break-words"
                        onKeyDownCapture={(e) => {
                            if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey) {
                                e.stopPropagation()
                                e.preventDefault()
                            }
                        }}
                    >
                        <SelectValue placeholder="Select a chatbot">
                            {selectedChatbot && chatbots.find(b => b.id === selectedChatbot)
                                ? `${chatbots.find(b => b.id === selectedChatbot)?.name} (${selectedChatbot})`
                                : undefined}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent
                        className="w-[var(--radix-select-trigger-width)]"
                        onKeyDownCapture={(e) => {
                            if ((e.target as HTMLElement).tagName === 'INPUT') return;
                            if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey) {
                                e.stopPropagation()
                                e.preventDefault()
                            }
                        }}
                    >
                        <div className="sticky -top-1 z-10 bg-white -mt-1 -mx-1 px-3 py-2 border-b mb-1">
                            <Input
                                placeholder="Search chatbot..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value)
                                    const input = e.target
                                    setTimeout(() => input.focus(), 0)
                                }}
                                onKeyDown={(e) => e.stopPropagation()}
                            />
                        </div>
                        {filteredChatbots.length === 0 ? (
                            <div className="p-2 text-sm text-center text-gray-500">No chatbots found</div>
                        ) : (
                            filteredChatbots.map(bot => (
                                <SelectItem key={bot.id} value={bot.id} className="h-auto whitespace-normal break-words py-2 pr-8">
                                    {bot.name} ({bot.id})
                                </SelectItem>
                            ))
                        )}
                    </SelectContent>
                </Select>

                <Button
                    className="bg-[#D91F27] hover:bg-[#b81a21] text-white gap-2"
                    onClick={() => setIsUploadOpen(true)}
                    disabled={!selectedChatbot}
                >
                    <Plus size={18} />
                    Add New File
                </Button>
            </div>

            {!selectedChatbot ? (
                <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
                    <div className="w-16 h-16 rounded-full border-2 border-gray-300 flex items-center justify-center mb-4">
                        <Info size={32} className="text-gray-400" />
                    </div>
                    <p>No chatbots selected.</p>
                </div>
            ) : (
                <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                    <Table>
                        <TableHeader className="bg-[#D91F27]">
                            <TableRow className="hover:bg-[#D91F27] border-none">
                                <TableHead className="text-white font-medium pl-6">File Name</TableHead>
                                <TableHead className="text-white font-medium">Status</TableHead>
                                <TableHead className="text-white font-medium">Last Update</TableHead>
                                <TableHead className="text-white font-medium text-right pr-6">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {files.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                        No files found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                files.map((file) => (
                                    <TableRow key={file.id} className="hover:bg-gray-50">
                                        <TableCell className="font-medium text-gray-900 pl-6">
                                            <a
                                                href={`${process.env.BUN_PUBLIC_API_URL}/admin/chatbots/${selectedChatbot}/qna/${file.id}/download`}
                                                className="hover:underline text-blue-600"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >{file.name}</a>
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const ingestion = ingestionByFileId[file.id]
                                                if (!ingestion) return null
                                                return (
                                                    <div className="flex flex-col gap-1">
                                                        <span className={cn(
                                                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium w-fit",
                                                            (ingestion.status === 'COMPLETED' || ingestion.status === 'NO_TASK') && "bg-green-100 text-green-700",
                                                            ingestion.status === 'FAILED' && "bg-red-100 text-red-700",
                                                            ingestion.status === 'PENDING' && "bg-yellow-100 text-yellow-700",
                                                            ingestion.status === 'PROCESSING' && "bg-blue-100 text-blue-700",
                                                        )} title={ingestion.status === 'FAILED' && ingestion.errorMessage ? ingestion.errorMessage : undefined}>
                                                            {(ingestion.status === 'PENDING' || ingestion.status === 'PROCESSING') && (
                                                                <Loader2 size={12} className="animate-spin" />
                                                            )}
                                                            {(ingestion.status === 'COMPLETED' || ingestion.status === 'NO_TASK') ? 'Ingested' : ingestion.status === 'FAILED' ? 'Failed' : ingestion.status === 'PENDING' ? 'Pending' : 'Processing'}
                                                        </span>
                                                        {ingestion.status === 'FAILED' && ingestion.errorMessage && (
                                                            <span className="text-xs text-red-600">{ingestion.errorMessage}</span>
                                                        )}
                                                    </div>
                                                )
                                            })()}
                                        </TableCell>
                                        <TableCell className="text-gray-600">{formatDateTime(file.lastUpdate)}</TableCell>
                                        <TableCell className="text-right pr-6">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-gray-400 hover:text-[#D91F27] hover:bg-red-50 disabled:opacity-50"
                                                onClick={() => handleDelete(file.id)}
                                                disabled={deleteMutation.isPending || ingestionByFileId[file.id]?.status === 'PENDING' || ingestionByFileId[file.id]?.status === 'PROCESSING'}
                                            >
                                                <Trash2 size={18} />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            <FileUploadModal
                isOpen={isUploadOpen}
                onClose={() => setIsUploadOpen(false)}
                onImport={handleImport}
                existingFiles={files.map(f => f.name)}
            />

            <FilePreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                onSave={handleSave}
                onReplace={() => {
                    setIsPreviewOpen(false)
                    setIsUploadOpen(true)
                }}
                data={previewData}
                error={validationError}
                isSaving={uploadMutation.isPending}
            />
            <LoadingModal isOpen={uploadMutation.isPending || deleteMutation.isPending} message={uploadMutation.isPending ? "Saving..." : "Removing..."} />
        </div>
    )
}
